import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LayoutGrid, Save } from "lucide-react";

export default function CollectionPage() {
  const [designs, setDesigns] = useState([]);
  const [active, setActive] = useState("All");
  const [drafts, setDrafts] = useState({});

  const load = () => api.getDesigns().then(setDesigns).catch(() => {});
  useEffect(() => { load(); }, []);

  const collections = useMemo(() => {
    const set = new Set(designs.map((d) => d.collection || "Season 01"));
    return ["All", ...Array.from(set)];
  }, [designs]);

  const filtered = active === "All" ? designs : designs.filter((d) => (d.collection || "Season 01") === active);

  const field = (d, key) => (drafts[d.id]?.[key] !== undefined ? drafts[d.id][key] : (d[key] || ""));
  const setField = (id, key, val) => setDrafts((p) => ({ ...p, [id]: { ...p[id], [key]: val } }));

  const save = async (d) => {
    const body = drafts[d.id];
    if (!body) return;
    try {
      const updated = await api.patchDesign(d.id, body);
      setDesigns((list) => list.map((x) => (x.id === d.id ? updated : x)));
      setDrafts((p) => { const n = { ...p }; delete n[d.id]; return n; });
      toast.success("Line sheet updated");
    } catch {
      toast.error("Could not save");
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 min-h-screen">
      <header className="pb-6 border-b border-white/10">
        <div className="overline-label">Concept Collection Architecture</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-1">Line Sheet</h2>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Structure your looks into drops. Assign look numbers, fabric compositions and titles —
          the backbone every buyer and investor will want to see.
        </p>
      </header>

      <div className="flex gap-2 flex-wrap mt-6" data-testid="collection-filters">
        {collections.map((c) => (
          <button
            key={c}
            data-testid={`collection-tab-${c}`}
            onClick={() => setActive(c)}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              active === c
                ? "bg-amber-500 text-black"
                : "border border-white/10 text-neutral-400 hover:text-white hover:border-amber-500/40"
            }`}
          >
            {c} {c !== "All" && <span className="opacity-60">· {designs.filter((d) => (d.collection || "Season 01") === c).length}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">
          <LayoutGrid className="mx-auto mb-4 text-neutral-700" size={32} />
          No looks here yet. Generate concepts in the Runway studio first.
        </div>
      ) : (
        <div className="space-y-4 mt-8">
          {filtered.map((d, idx) => {
            const dirty = !!drafts[d.id];
            return (
              <div key={d.id} className="card-lux rounded-xl p-4 flex flex-col sm:flex-row gap-5" data-testid={`linesheet-row-${d.id}`}>
                <img src={`data:image/png;base64,${d.image_base64}`} alt={d.title} className="w-full sm:w-28 h-40 sm:h-36 object-cover rounded-lg flex-shrink-0" />
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="overline-label text-[9px]">Look No.</label>
                    <input
                      value={field(d, "look_number")}
                      onChange={(e) => setField(d.id, "look_number", e.target.value)}
                      placeholder={`${String(idx + 1).padStart(2, "0")}`}
                      className="w-full bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 font-mono outline-none"
                    />
                  </div>
                  <div>
                    <label className="overline-label text-[9px]">Title</label>
                    <input
                      value={field(d, "title")}
                      onChange={(e) => setField(d.id, "title", e.target.value)}
                      className="w-full bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 outline-none"
                    />
                  </div>
                  <div>
                    <label className="overline-label text-[9px]">Collection</label>
                    <input
                      value={field(d, "collection")}
                      onChange={(e) => setField(d.id, "collection", e.target.value)}
                      className="w-full bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 outline-none"
                    />
                  </div>
                  <div>
                    <label className="overline-label text-[9px]">Fabric / Composition</label>
                    <input
                      value={field(d, "fabric")}
                      onChange={(e) => setField(d.id, "fabric", e.target.value)}
                      placeholder="e.g. 100% silk duchesse, 380 GSM"
                      className="w-full bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 font-mono text-sm outline-none"
                    />
                  </div>
                </div>
                <div className="flex sm:flex-col justify-end">
                  <button
                    data-testid={`save-linesheet-${d.id}`}
                    onClick={() => save(d)}
                    disabled={!dirty}
                    className="btn-gold rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm h-fit"
                  >
                    <Save size={15} /> Save
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
